// src/components/OutlookMail/OutlookMailSection.tsx
// =================================================================
// OUTLOOK MAIL SECTION — main orchestrator
// =================================================================
// Renders one of three states based on result.status:
//  - "preview"  → interactive email compose card (adapted from v0 email-preview)
//  - "success"  → compact result toast (OutlookMailResult)
//  - "error"    → error card (OutlookMailError)
// =================================================================

import * as React from "react";
import { useState, useCallback, useRef, useEffect } from "react";
import { X, Paperclip, FileText, Send, Save, Loader2, Wand2 } from "lucide-react";
import OutlookLogo from "../../../assets/Outlook_Logo.svg";
import {
  OutlookMailResult,
  EmailRecipient,
  EmailData,
  directApiResponseToOutlookMailResult,
} from "../../interfaces/outlookMailTypes";
import { sendOutlookMail, searchPeople } from "../../api/outlookMailApi";
import { useAuth } from "../../contexts/AuthContext";
import { getDocumentFileBytes, getDocumentName } from "../../helpers/word";
import { createLogger } from "../../utils/logger";
import OutlookMailResultCard from "./OutlookMailResult";
import OutlookMailError from "./OutlookMailError";
import {
  outlookColors,
  previewCardStyle,
  previewHeaderStyle,
  fieldRowStyle,
  fieldLabelStyle,
  recipientChipStyle,
  recipientChipTextStyle,
  recipientChipRemoveStyle,
  inputFieldStyle,
  attachmentRowStyle,
  attachmentBadgeStyle,
  actionsRowStyle,
  primaryButtonSendStyle,
  primaryButtonDraftStyle,
  beautifyButtonStyle,
} from "./outlookMailStyles";

const log = createLogger("OutlookMailSection");

/** Prefix for beautify prompts — detected in MessageBubble to render a styled indicator. */
export const BEAUTIFY_EMAIL_PREFIX = "[beautify-email]\n";

// ─── BeautifyIndicator — shown in user message bubble instead of raw prompt ──

const SPARKLE_STYLE_ID = "beautify-sparkle-keyframes";

/**
 * Compact indicator rendered in the user message bubble when a beautify
 * prompt is detected.  Custom wand SVG with the three sparkle crosses
 * animated (staggered opacity + scale pulse).  No box/border — inline only.
 */
export function BeautifyIndicator() {
  useEffect(() => {
    if (document.getElementById(SPARKLE_STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = SPARKLE_STYLE_ID;
    style.textContent = `
      @keyframes beautifySparkle {
        0%, 100% { opacity: 0.4; transform: scale(0.8); }
        50% { opacity: 1; transform: scale(1.1); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const sparkleGroupStyle = (delay: string): React.CSSProperties => ({
    animation: `beautifySparkle 2s ease-in-out ${delay} infinite`,
    transformOrigin: "center",
  });

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke={outlookColors.blue}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: 16, height: 16 }}
      >
        {/* Wand body — static */}
        <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72" />
        <path d="m14 7 3 3" />
        {/* Sparkle crosses — animated with staggered pulse */}
        <g style={sparkleGroupStyle("0s")}>
          <path d="M5 6v4" />
          <path d="M3 8h4" />
        </g>
        <g style={sparkleGroupStyle("0.4s")}>
          <path d="M19 14v4" />
          <path d="M17 16h4" />
        </g>
        <g style={sparkleGroupStyle("0.8s")}>
          <path d="M10 2v2" />
          <path d="M9 3h2" />
        </g>
      </svg>
      <span style={{ fontSize: 13, fontWeight: 500, color: outlookColors.blue }}>Beautify...</span>
    </div>
  );
}

// ─── Props ──────────────────────────────────────────────────────────────

interface OutlookMailSectionProps {
  result: OutlookMailResult;
  onSendComplete?: (result: OutlookMailResult) => void;
  onSendPrompt?: (prompt: string) => void;
  isStreaming?: boolean;
}

// ─── Helpers: convert string recipients to EmailRecipient[] ────────────

function toEmailRecipients(addresses: string[]): EmailRecipient[] {
  return addresses.map((addr) => {
    const atIdx = addr.indexOf("@");
    return {
      email: addr,
      name: atIdx > 0 ? addr.substring(0, atIdx) : addr,
    };
  });
}

// ─── RecipientChip ──────────────────────────────────────────────────────

function RecipientChip({
  recipient,
  onRemove,
}: {
  recipient: EmailRecipient;
  onRemove: () => void;
}) {
  const displayName = recipient.name || recipient.email.split("@")[0];
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        ...recipientChipStyle,
        ...(hovered ? { background: outlookColors.secondaryBgHover } : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={recipientChipTextStyle}>{displayName}</span>
      <button
        onClick={onRemove}
        style={{
          ...recipientChipRemoveStyle,
          ...(hovered ? { opacity: 1 } : {}),
        }}
        aria-label={`Remove ${displayName}`}
      >
        <X size={12} />
      </button>
    </div>
  );
}

// ─── SuggestionItem ────────────────────────────────────────────────────

function SuggestionItem({
  suggestion,
  onSelect,
}: {
  suggestion: { display_name: string; email_address: string; job_title?: string };
  onSelect: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onMouseDown={(e: React.MouseEvent) => {
        e.preventDefault();
        onSelect();
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1px",
        width: "100%",
        padding: "8px 12px",
        border: "none",
        background: hovered ? "#F3F4F6" : "transparent",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span style={{ fontSize: "12px", fontWeight: 500, color: "#1A1A2E" }}>
        {suggestion.display_name}
      </span>
      <span style={{ fontSize: "11px", color: "#6B7280" }}>
        {suggestion.email_address}
        {suggestion.job_title ? ` \u00B7 ${suggestion.job_title}` : ""}
      </span>
    </button>
  );
}

// ─── RecipientInput ────────────────────────────────────────────────────

function RecipientInput({
  onAdd,
  placeholder,
}: {
  onAdd: (recipient: EmailRecipient) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<
    Array<{ display_name: string; email_address: string; job_title?: string }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { accessToken } = useAuth();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (value.length < 2 || !accessToken) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        setIsLoading(true);
        try {
          const results = await searchPeople(value, accessToken, 5);
          setSuggestions(results);
          setShowSuggestions(results.length > 0);
        } catch {
          setSuggestions([]);
          setShowSuggestions(false);
        } finally {
          setIsLoading(false);
        }
      }, 300);
    },
    [accessToken]
  );

  const handleSelect = useCallback(
    (suggestion: { display_name: string; email_address: string }) => {
      onAdd({ email: suggestion.email_address, name: suggestion.display_name });
      setQuery("");
      setSuggestions([]);
      setShowSuggestions(false);
      inputRef.current?.focus();
    },
    [onAdd]
  );

  // Auto-add email on blur if it contains @
  const handleBlur = useCallback(() => {
    if (query.trim() && query.includes("@")) {
      onAdd({ email: query.trim() });
      setQuery("");
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [query, onAdd]);

  // Allow manual email entry via Enter or Space key
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && query.trim().includes("@")) {
        e.preventDefault();
        onAdd({ email: query.trim() });
        setQuery("");
        setSuggestions([]);
        setShowSuggestions(false);
      }
      if (e.key === "Escape") {
        setShowSuggestions(false);
      }
    },
    [query, onAdd]
  );

  // Close suggestions on click outside
  useEffect(() => {
    if (!showSuggestions) return;
    const handler = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.parentElement?.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSuggestions]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => {
          if (suggestions.length > 0) setShowSuggestions(true);
        }}
        placeholder={placeholder || "Add recipient..."}
        style={{
          border: "none",
          outline: "none",
          background: "transparent",
          fontSize: "12px",
          color: "#1A1A2E",
          width: "100%",
          padding: "2px 0",
        }}
      />
      {showSuggestions && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 50,
            background: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            maxHeight: "200px",
            overflowY: "auto",
            marginTop: "4px",
          }}
        >
          {suggestions.map((s, idx) => (
            <SuggestionItem key={s.email_address} suggestion={s} onSelect={() => handleSelect(s)} />
          ))}
        </div>
      )}
      {isLoading && (
        <div
          style={{
            position: "absolute",
            right: 4,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <Loader2 size={12} style={{ animation: "spin 1s linear infinite", color: "#0078D4" }} />
        </div>
      )}
    </div>
  );
}

// ─── EmailPreviewCard (preview state) ──────────────────────────────────

function EmailPreviewCard({
  result,
  onSendComplete,
  onSendPrompt,
  isStreaming,
}: {
  result: OutlookMailResult;
  onSendComplete?: (result: OutlookMailResult) => void;
  onSendPrompt?: (prompt: string) => void;
  isStreaming?: boolean;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const initialBodyRef = useRef(result.bodyPreview || "");

  const [email, setEmail] = useState<EmailData>(() => ({
    to: toEmailRecipients(result.toRecipients),
    cc: result.ccRecipients.length > 0 ? toEmailRecipients(result.ccRecipients) : undefined,
    subject: result.subject,
    body: result.bodyPreview || "",
    attachDocument: result.attachOpenDocument ?? false,
    documentName: result.attachmentName || undefined,
  }));

  const [showCc, setShowCc] = useState((result.ccRecipients?.length ?? 0) > 0);
  const [draftHovered, setDraftHovered] = useState(false);
  const [sendHovered, setSendHovered] = useState(false);
  const [beautifyHovered, setBeautifyHovered] = useState(false);
  const [isBeautifying, setIsBeautifying] = useState(false);
  const prevStreamingRef = useRef(!!isStreaming);
  const [sendingAction, setSendingAction] = useState<"draft" | "send" | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  // Clear beautifying state when streaming ends (true → false)
  useEffect(() => {
    if (prevStreamingRef.current && !isStreaming) {
      setIsBeautifying(false);
    }
    prevStreamingRef.current = !!isStreaming;
  }, [isStreaming]);

  // Inject gradient animation keyframes
  useEffect(() => {
    const id = "beautify-gradient-keyframes";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      @keyframes beautifyGradientSpin {
        0% { background-position: 0% 50%; }
        100% { background-position: 300% 50%; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const { accessToken } = useAuth();

  const updateEmail = useCallback((updates: Partial<EmailData>) => {
    setEmail((prev) => ({ ...prev, ...updates }));
  }, []);

  const removeRecipient = useCallback((field: "to" | "cc", index: number) => {
    setEmail((prev) => ({
      ...prev,
      [field]: (prev[field] ?? []).filter((_: EmailRecipient, i: number) => i !== index),
    }));
  }, []);

  const addRecipient = useCallback((field: "to" | "cc", recipient: EmailRecipient) => {
    setEmail((prev) => {
      const existing = prev[field] ?? [];
      if (
        existing.some(
          (r: EmailRecipient) => r.email.toLowerCase() === recipient.email.toLowerCase()
        )
      ) {
        return prev; // duplicate — no change
      }
      return { ...prev, [field]: [...existing, recipient] };
    });
  }, []);

  const handleAction = useCallback(
    async (mode: "draft" | "send") => {
      if (!onSendComplete || !accessToken) return;
      setSendingAction(mode);
      setSendError(null);

      try {
        // Fetch document bytes if attachment is toggled on
        let attachmentBase64: string | undefined;
        let attachmentName: string | undefined;
        if (email.attachDocument) {
          try {
            const fileBytes = await getDocumentFileBytes();
            const bytes = new Uint8Array(fileBytes);
            let binary = "";
            for (let i = 0; i < bytes.length; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            attachmentBase64 = btoa(binary);
            attachmentName = email.documentName || getDocumentName();
            log.info("Document attachment fetched for direct send", {
              documentName: attachmentName,
              base64Length: attachmentBase64.length,
            });
          } catch (err) {
            log.error("Failed to fetch document for attachment", { error: String(err) });
            setSendError("Could not read the document for attachment. Try again.");
            setSendingAction(null);
            return;
          }
        }

        // Read the current body from the contentEditable div (not from state)
        const currentBody = bodyRef.current?.innerHTML || email.body;

        const response = await sendOutlookMail(
          {
            action: mode,
            subject: email.subject,
            body_html: currentBody,
            to: email.to.map((r) => r.email),
            cc: email.cc?.length ? email.cc.map((r) => r.email) : undefined,
            attachment_base64: attachmentBase64,
            attachment_name: attachmentName,
          },
          accessToken
        );

        log.info("Direct mail action completed", {
          action: mode,
          subject: email.subject,
        });

        const successResult = directApiResponseToOutlookMailResult(
          response,
          `direct-${Date.now()}`,
          currentBody
        );
        onSendComplete(successResult);
      } catch (err: any) {
        log.error("Direct mail action failed", { error: err.message || String(err) });
        setSendError(err.message || "Failed to send email. Please try again.");
      } finally {
        setSendingAction(null);
      }
    },
    [email, accessToken, onSendComplete]
  );

  const handleBeautify = useCallback(() => {
    if (!onSendPrompt) return;

    const currentBody = bodyRef.current?.innerHTML || email.body;
    const toList = email.to.map((r) => r.email).join(", ");
    const ccList = email.cc?.map((r) => r.email).join(", ");

    const lines = [
      `Beautify the formatting of this email preview. Here is the current content:`,
      ``,
      `Subject: ${email.subject}`,
      ...(toList ? [`To: ${toList}`] : []),
      ...(ccList ? [`CC: ${ccList}`] : []),
      ...(email.attachDocument ? [`Attach open document: yes`] : []),
      ``,
      `Body:`,
      currentBody,
      ``,
      `Restyle the body HTML using a minimalist layout with a professional monochromatic color palette and plenty of negative space for a modern look, ensuring visual weight and contrast are optimized for a crisp white Outlook background. Preserve all text content exactly — only transform the visual presentation.`,
    ];

    setIsBeautifying(true);
    onSendPrompt(BEAUTIFY_EMAIL_PREFIX + lines.join("\n"));
  }, [email, onSendPrompt]);

  const isSending = sendingAction !== null;
  const isSendDisabled = isSending || email.to.length === 0;
  const isDraftDisabled = isSending;
  const isBeautifyDisabled = !!isStreaming || isSending || isBeautifying;

  return (
    <div style={previewCardStyle}>
      {/* Header */}
      <div style={previewHeaderStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <OutlookLogo style={{ width: 18, height: 18, flexShrink: 0, overflow: "visible" }} />
          <span
            style={{
              fontSize: "12px",
              fontWeight: 500,
              color: outlookColors.foreground,
            }}
          >
            New Message
          </span>
        </div>
      </div>

      {/* To Field */}
      <div style={fieldRowStyle}>
        <label style={fieldLabelStyle}>To</label>
        <div
          style={{
            display: "flex",
            flex: 1,
            flexWrap: "wrap",
            alignItems: "center",
            gap: "6px",
            minHeight: "28px",
          }}
        >
          {email.to.map((recipient, index) => (
            <RecipientChip
              key={recipient.email}
              recipient={recipient}
              onRemove={() => removeRecipient("to", index)}
            />
          ))}
          <RecipientInput onAdd={(r) => addRecipient("to", r)} placeholder="Add recipient..." />
        </div>
        {!showCc && (
          <button
            onClick={() => setShowCc(true)}
            style={{
              flexShrink: 0,
              fontSize: "12px",
              fontWeight: 500,
              color: outlookColors.blue,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              fontFamily: "inherit",
            }}
          >
            Cc
          </button>
        )}
      </div>

      {/* CC Field */}
      {showCc && (
        <div style={fieldRowStyle}>
          <label style={fieldLabelStyle}>Cc</label>
          <div
            style={{
              display: "flex",
              flex: 1,
              flexWrap: "wrap",
              alignItems: "center",
              gap: "6px",
              minHeight: "28px",
            }}
          >
            {(email.cc ?? []).map((recipient, index) => (
              <RecipientChip
                key={recipient.email}
                recipient={recipient}
                onRemove={() => removeRecipient("cc", index)}
              />
            ))}
            <RecipientInput onAdd={(r) => addRecipient("cc", r)} placeholder="Add CC..." />
          </div>
        </div>
      )}

      {/* Subject Field */}
      <div style={fieldRowStyle}>
        <label style={fieldLabelStyle}>Subject</label>
        <input
          type="text"
          value={email.subject}
          onChange={(e) => updateEmail({ subject: e.target.value })}
          style={inputFieldStyle}
          placeholder="Add a subject"
        />
      </div>

      {/* Body — uncontrolled contentEditable to avoid cursor jump on re-render */}
      <div style={{ borderTop: `1px solid ${outlookColors.borderDivider}` }}>
        <div
          ref={bodyRef}
          contentEditable
          suppressContentEditableWarning
          dangerouslySetInnerHTML={{ __html: initialBodyRef.current }}
          style={{
            minHeight: "240px",
            maxHeight: "800px",
            overflowY: "auto",
            padding: "12px",
            fontSize: "13px",
            lineHeight: "1.6",
            color: outlookColors.foreground,
            outline: "none",
            cursor: "text",
          }}
        />
      </div>

      {/* Attachment Toggle */}
      <label style={attachmentRowStyle}>
        <input
          type="checkbox"
          checked={email.attachDocument ?? false}
          onChange={(e) => {
            const checked = e.target.checked;
            if (checked && !email.documentName) {
              updateEmail({ attachDocument: true, documentName: getDocumentName() });
            } else {
              updateEmail({ attachDocument: checked });
            }
          }}
          style={{ accentColor: outlookColors.blue }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Paperclip size={14} color={outlookColors.mutedForeground} />
          <span style={{ fontSize: "12px", color: outlookColors.foreground }}>
            Attach open document
          </span>
        </div>
        {email.attachDocument && email.documentName && (
          <div style={attachmentBadgeStyle}>
            <FileText size={12} color={outlookColors.blue} />
            <span
              style={{
                maxWidth: "120px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap" as const,
                fontSize: "12px",
                color: outlookColors.foreground,
              }}
            >
              {email.documentName}
            </span>
          </div>
        )}
      </label>

      {/* Error message */}
      {sendError && (
        <div
          style={{
            padding: "8px 12px",
            borderTop: `1px solid ${outlookColors.separator}`,
            fontSize: "12px",
            color: outlookColors.errorColor,
            background: outlookColors.errorBg,
          }}
        >
          {sendError}
        </div>
      )}

      {/* Actions */}
      <div style={actionsRowStyle}>
        <button
          onClick={() => handleAction("draft")}
          disabled={isDraftDisabled}
          onMouseEnter={() => setDraftHovered(true)}
          onMouseLeave={() => setDraftHovered(false)}
          style={{
            ...primaryButtonDraftStyle,
            ...(isDraftDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
            ...(draftHovered && !isDraftDisabled
              ? { background: outlookColors.secondaryBgHover }
              : {}),
          }}
        >
          {sendingAction === "draft" ? (
            <>
              <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
              Saving...
            </>
          ) : (
            <>
              <Save size={14} />
              Save as Draft
            </>
          )}
        </button>
        <button
          onClick={() => handleAction("send")}
          disabled={isSendDisabled}
          onMouseEnter={() => setSendHovered(true)}
          onMouseLeave={() => setSendHovered(false)}
          style={{
            ...primaryButtonSendStyle,
            ...(isSendDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
            ...(sendHovered && !isSendDisabled ? { background: outlookColors.blueHover } : {}),
          }}
        >
          {sendingAction === "send" ? (
            <>
              <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
              Sending...
            </>
          ) : (
            <>
              <Send size={14} />
              Send
            </>
          )}
        </button>
        {onSendPrompt && (
          <button
            onClick={handleBeautify}
            onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
            disabled={isBeautifyDisabled}
            onMouseEnter={() => setBeautifyHovered(true)}
            onMouseLeave={() => setBeautifyHovered(false)}
            style={{
              ...beautifyButtonStyle,
              position: "relative",
              overflow: "hidden",
              ...(isBeautifyDisabled && !isBeautifying
                ? { opacity: 0.5, cursor: "not-allowed" }
                : {}),
              ...(isBeautifying ? { cursor: "default", borderColor: "transparent" } : {}),
              ...(beautifyHovered && !isBeautifyDisabled
                ? { background: outlookColors.secondaryBgHover }
                : {}),
            }}
          >
            {/* Animated gradient border — visible when beautifying */}
            {isBeautifying && (
              <>
                <span
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: "6px",
                    background: "linear-gradient(90deg, #0078D4, #75B6FF, #0078D4, #75B6FF)",
                    backgroundSize: "300% 100%",
                    filter: "blur(4px)",
                    animation: "beautifyGradientSpin 3s linear infinite",
                    pointerEvents: "none",
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    top: "2px",
                    left: "2px",
                    right: "2px",
                    bottom: "2px",
                    borderRadius: "5px",
                    background: "#FFFFFF",
                    pointerEvents: "none",
                  }}
                />
              </>
            )}
            {/* Content — always on top */}
            <span
              style={{
                position: "relative",
                zIndex: 1,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Wand2 size={14} color={isBeautifying ? outlookColors.blue : "#374151"} />
              {isBeautifying ? "Beautifying..." : "Beautify"}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────

const OutlookMailSection: React.FC<OutlookMailSectionProps> = ({
  result,
  onSendComplete,
  onSendPrompt,
  isStreaming,
}) => {
  if (result.status === "preview") {
    return (
      <EmailPreviewCard
        result={result}
        onSendComplete={onSendComplete}
        onSendPrompt={onSendPrompt}
        isStreaming={isStreaming}
      />
    );
  }

  if (result.status === "error") {
    return <OutlookMailError result={result} />;
  }

  // status === "success"
  return <OutlookMailResultCard result={result} />;
};

export default OutlookMailSection;
